import type {
  DensityPreset,
  LevelData,
  LevelTrack,
  PuzzleDifficulty,
  SavedGame,
  Screen,
  TrainSettings,
} from './types';
import { LEVEL_DATA } from './puzzles';

export interface ResumableItem {
  /** Storage key for the saved game (also used to navigate / clear). */
  key: string;
  screen: Screen;
  label: string;
  updatedAt: number;
}

const DENSITY_VALUES: readonly DensityPreset[] = ['sparse', 'normal', 'dense'];
const TRACKS: readonly LevelTrack[] = ['progression', 'daily'];
const DIFFICULTIES: readonly PuzzleDifficulty[] = ['casual', 'advanced'];

function findLevel(
  levelId: string,
): {
  level: LevelData;
  track: LevelTrack;
  difficulty: PuzzleDifficulty;
  index: number;
} | null {
  for (const track of TRACKS) {
    for (const difficulty of DIFFICULTIES) {
      const pool = LEVEL_DATA[track][difficulty];
      const index = pool.findIndex((l) => l.id === levelId);
      if (index >= 0) {
        return { level: pool[index], track, difficulty, index };
      }
    }
  }
  return null;
}

function parseLevelsKey(key: string): {
  screen: Screen;
  track: LevelTrack;
  index: number;
} | null {
  if (key.includes(':')) {
    const [levelId, puzzleId] = key.split(':');
    const found = findLevel(levelId);
    if (!found) return null;
    const subIndex = found.level.puzzles.findIndex((p) => p.id === puzzleId);
    if (subIndex < 0) return null;
    return {
      screen: {
        type: 'level',
        mode: 'multi-sub',
        track: found.track,
        difficulty: found.difficulty,
        levelIndex: found.index,
        subIndex,
        entry: 'resume',
      },
      track: found.track,
      index: found.index,
    };
  }

  const found = findLevel(key);
  if (!found) return null;
  return {
    screen: {
      type: 'level',
      mode: 'multi-sub',
      track: found.track,
      difficulty: found.difficulty,
      levelIndex: found.index,
      subIndex: 0,
      entry: 'resume',
    },
    track: found.track,
    index: found.index,
  };
}

function parseTrainKey(
  key: string,
  trainSettings: TrainSettings,
): { screen: Screen; label: string } | null {
  // Format: train_${size}_${encodeURIComponent(seed)}_${density}
  // Seeds may contain underscores, so peel size off the front and density
  // off the back.
  const parts = key.split('_');
  if (parts.length < 4) return null;
  const sizeStr = parts[1];
  const density = parts[parts.length - 1] as DensityPreset;
  const seed = decodeURIComponent(parts.slice(2, -1).join('_'));
  const size = Number(sizeStr);
  if (!Number.isFinite(size) || !DENSITY_VALUES.includes(density)) return null;

  return {
    screen: {
      type: 'level',
      mode: 'train',
      size,
      seed,
      density,
      strict: trainSettings.strict,
      hintCount: trainSettings.hintCount,
      validateCount: trainSettings.validateCount,
      autoMark: trainSettings.autoMark,
      entry: 'resume',
    },
    label: `Practice · ${size}×${size}`,
  };
}

/** Parse any save/completion key into a `{ screen, label }`, or null if the
 *  key refers to content we can't navigate back to (missing level, tutorial,
 *  etc). The returned screen uses `entry: 'resume'`; callers that navigate to
 *  a completed puzzle should clone the screen with `entry: 'view'`. */
export function parseAnyKey(
  key: string,
  dailyIndex: number,
  trainSettings: TrainSettings,
): { screen: Screen; label: string } | null {
  if (key.startsWith('tutorial_')) return null;

  if (key.startsWith('train_')) {
    return parseTrainKey(key, trainSettings);
  }

  const parsed = parseLevelsKey(key);
  if (!parsed) return null;
  const isDaily = parsed.track === 'daily';
  const isDailyToday = isDaily && parsed.index === dailyIndex;
  return {
    screen: parsed.screen,
    label: isDailyToday
      ? 'Daily puzzle'
      : isDaily
        ? `Daily · ${parsed.index + 1}`
        : `Level ${parsed.index + 1}`,
  };
}

export function parseSaveKey(
  key: string,
  savedGame: SavedGame,
  dailyIndex: number,
  trainSettings: TrainSettings,
): ResumableItem | null {
  const parsed = parseAnyKey(key, dailyIndex, trainSettings);
  if (!parsed) return null;
  return {
    key,
    screen: parsed.screen,
    label: parsed.label,
    updatedAt: savedGame.updatedAt ?? 0,
  };
}

export function getLatestResumable(
  savedGames: Record<string, SavedGame>,
  dailyIndex: number,
  trainSettings: TrainSettings,
): ResumableItem | null {
  let best: ResumableItem | null = null;
  for (const [key, game] of Object.entries(savedGames)) {
    const item = parseSaveKey(key, game, dailyIndex, trainSettings);
    if (!item) continue;
    if (!best || item.updatedAt > best.updatedAt) best = item;
  }
  return best;
}
